<script setup lang="ts">
import bannerImgDefault from "/img/banner/banner-catalogue.jpg";
import bannerImgId from "/img/banner/banner-single-course.jpg";

const route = useRoute();

const isSearchPage = computed(() => route.path.startsWith("/search"));
const isCourseDetail = computed(() => route.name === "course-id");
const isCatalogPage = computed(() => route.path === "/" || route.name === "catalog");

const bannerImg = computed(() => isCourseDetail.value ? bannerImgId : bannerImgDefault);
const showAdditionalText = computed(() => isCatalogPage.value);

const { totalCourses, load } = useTotalCourses();

const animatedCount = ref(0);

function animateToTotal(target: number) {
	const duration = 800;
	const startTime = performance.now();

	const animate = (currentTime: number) => {
		const progress = Math.min((currentTime - startTime) / duration, 1);
		animatedCount.value = Math.floor(progress * target);
		if (progress < 1) requestAnimationFrame(animate);
	};

	requestAnimationFrame(animate);
}

onMounted(async () => {
	if (!isCatalogPage.value) return;
	await load();
	if (totalCourses.value !== null) animateToTotal(totalCourses.value);
});

watch(totalCourses, (val) => {
	if (isCatalogPage.value && val !== null && val > 0) animateToTotal(val);
});

const bannerTitle = computed(() => {
	if (!isCatalogPage.value) return "";
	const display = animatedCount.value > 0 ? animatedCount.value : totalCourses.value;
	if (!display) return "";
	return `${display.toLocaleString("fr-FR")} prestations, pour se former autrement`;
});
</script>

<template>
	<div v-if="!isSearchPage"
		:class="{ 'hero-banner': true, 'hero-banner-id': isCourseDetail }"
		:style="{ backgroundImage: `url(${bannerImg})` }">
		<div class="hero-banner-content">
			<h1 v-if="bannerTitle">
				{{ bannerTitle }}
			</h1>

			<div v-if="showAdditionalText"
				class="center">
				<p>
					Nous publions de nouvelles offres régulièrement.
					<a href="/ifen/modalites-dinscription-foco/"
						target="_blank">
						En savoir plus
					</a>
				</p>
			</div>
		</div>

		<svg v-if="isCatalogPage"
			class="curve curve-bottom curve-catalog"
			xmlns="http://www.w3.org/2000/svg"
			xmlns:xlink="http://www.w3.org/1999/xlink"
			width="100%"
			height="96.324"
			viewBox="0 0 1920.578 96.324"
			preserveAspectRatio="none">
			<g id="Groupe_défilant_28"
				transform="translate(0 7)"
				style="isolation: isolate">
				<path id="Tracé_946"
					d="M-8169,1328.734v-78.949s455.5,77.411,935.645,78.875,984.934-73.02,984.934-73.02v106.469H-8169Z"
					transform="translate(8169 -1249.785)"
					fill="#fff" />
			</g>
		</svg>

		<svg v-if="isCourseDetail"
			class="curve curve-bottom curve-desktop curve-single"
			xmlns="http://www.w3.org/2000/svg"
			width="100%"
			height="77.168"
			viewBox="0 0 1600 77.168"
			preserveAspectRatio="none">
			<path id="Tracé_454"
				d="M0,0H1600V29.331S1204.669,77.168,804.669,77.168,0,29.331,0,29.331Z"
				transform="translate(1600 77.168) rotate(180)"
				fill="#fff" />
		</svg>

		<svg v-if="isCourseDetail"
			class="curve curve-bottom curve-mobile curve-single"
			xmlns="http://www.w3.org/2000/svg"
			width="100%"
			height="77.168"
			viewBox="0 0 1600 77.168">
			<path id="Tracé_454"
				d="M0,0H1600V29.331S1204.669,77.168,804.669,77.168,0,29.331,0,29.331Z"
				transform="translate(1600 77.168) rotate(180)"
				fill="#fff" />
		</svg>
	</div>
</template>

<style scoped lang="scss">
@use '@/assets/styles/utils/mixins' as *;
@use '@/assets/styles/utils/variables' as *;

.hero-banner-id {
    height: 150px;
    margin-bottom: 30px;

    @include large {
        height: 250px;
        margin-bottom: 0;
    }
}
</style>
